import React, { RefObject } from 'react';
import { connect } from 'react-redux';
import { AutoSizer } from 'react-virtualized';
import { ITicker } from '@kattana-io/common';
import TradingViewChart from '../../components/Chart/TradingViewChart';
import LightweightChart from '../../components/Chart/LeanChart';
import withDimensions from '../../components/withSize';
import { IChartPickerSelection } from '../../reducers/chart_picker';
import { actionAddGrid, IAddPayload } from '../../actions/grid';
import { IGridItem, IGridState } from '../../reducers/grid';
import { ICurrentState } from '../../reducers/current';
import { IStore } from '../../reducers';
import { ILayoutState } from '../../reducers/layout';

import './styles.scss';

const Chart = withDimensions(TradingViewChart);
const LightChart = withDimensions(LightweightChart);

/**
 * Constants
 */
const DISTANCE_BETWEEN_ROWS = 1.5;
const DISTANCE_BETWEEN_COLUMNS = 2.5;
const HEADER_AND_FOOTER_HEIGHT = 80;

interface ChartDisplayProps {
  selected: IChartPickerSelection;
  isMobile: boolean;
  actionAddGrid: (payload: IAddPayload) => void;
  grid: IGridState;
  current: ICurrentState;
  layout: ILayoutState;
  // width: number
}

/**
 * Create array like [4] => [1,2,3,4]
 * @param target
 * @returns
 */
const createArray = (target: number): number[] => {
  const result: number[] = [];
  for (let index = 1; index <= target; index++) {
    result.push(index);
  }
  return result;
};

class ChartDisplay extends React.Component<ChartDisplayProps> {
  private rowRef: RefObject<any>;

  constructor(props: ChartDisplayProps) {
    super(props);
    this.rowRef = React.createRef();
  }

  componentDidUpdate(prevProps: ChartDisplayProps) {
    if (
      prevProps.selected.cols !== this.props.selected.cols ||
      prevProps.selected.rows !== this.props.selected.rows
    ) {
      const { selected } = this.props;
      const rows = createArray(selected.rows);
      const cols = createArray(selected.cols);

      const net: {
        [key: string]: IGridItem;
      } = {};
      const ticker: ITicker & any = {
        ...this.props.current,
      };

      rows.forEach((row: number) => {
        cols.forEach((col: number) => {
          const key = `${row}-${col}`;

          let primary = false;
          if (row === 1 && col === 1) {
            primary = true;
          }

          net[key] = {
            key,
            primary,
            ticker,
            exchange: this.props.current.exchange,
          };
        });
      });

      this.props.actionAddGrid({ rows, cols, net });
    }
  }

  render() {
    const { selected, isMobile } = this.props;
    const singleChart = selected.cols === 1 && selected.rows === 1;

    /**
     * Expected behavior: ignore multicharts on mobile
     */
    if (isMobile) {
      return (
        <div className='chart chart--single'>
          <Chart isMobile={true} />
        </div>
      );
    }
    if (singleChart) {
      return (
        <div className='chart chart--single'>
          <Chart />
        </div>
      );
    }

    /**
     * Multi chart layout (it will rerender if width or height is changed.
     */
    const { cols, rows } = this.props.grid;

    return (
      <div className='chart'>
        <div ref={this.rowRef} className='chartsDisplay'>
          {rows.map((row: number) => (
            <div key={row} className={`row row_count-${selected.rows}`}>
              {cols.map((col: number) => {
                const key = `${row}-${col}`;
                const item = this.props.grid.net[key];

                return (
                  item &&
                  item.key && (
                    <div key={key} className='chart__cell'>
                      <AutoSizer>
                        {({ width, height }) => {
                          return (
                            <LightChart
                              key={item.key}
                              itemKey={item.key}
                              maxHeight={height - DISTANCE_BETWEEN_ROWS}
                              maxWidth={width - DISTANCE_BETWEEN_COLUMNS * 2}
                            />
                          );
                        }}
                      </AutoSizer>
                    </div>
                  )
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: IStore) => ({
  grid: state.grid,
  current: state.current,
  layout: state.layout,
});

export default connect(mapStateToProps, { actionAddGrid })(ChartDisplay);
